"use client";
/**
 * Dine-in "open bill" discovery for HomeScreen.
 *
 * When a guest scans a table QR and that table already has an active order we
 * want to take them straight to the live bill — *once*. The original
 * implementation guarded that redirect with a `useRef`, which is reset on every
 * HomeScreen remount. Because every exit from DineInOrderScreen navigates back
 * to "/", the guest was bounced to the order screen again the instant Home
 * remounted, making it impossible to reach the menu and build a cart.
 *
 * The auto-open marker therefore lives in `sessionStorage` (same lifetime and
 * slug scoping as the table context itself, see `@/store/table`) and is keyed by
 * `branchId:tableId:orderId` — a *new* order at the same table still earns one
 * courtesy auto-open.
 *
 * The hook always reports the active order id so Home can render a persistent,
 * non-blocking "open bill" banner once the auto-open has been spent.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { tableApi } from "@/api/services";
import { scopedStorageKey } from "@/lib/storageScope";

const AUTO_OPEN_KEY = "tappya_table_bill_autoopen_v2";

/** Cap the marker list so a long-lived tab can't grow storage without bound. */
const MAX_MARKERS = 20;

type AutoOpenState = { opened: string[] };

function autoOpenStorageKey(): string {
  return scopedStorageKey(AUTO_OPEN_KEY);
}

function readState(): AutoOpenState {
  try {
    const raw = sessionStorage.getItem(autoOpenStorageKey());
    if (!raw) return { opened: [] };
    const parsed = JSON.parse(raw) as AutoOpenState;
    if (!parsed || !Array.isArray(parsed.opened)) return { opened: [] };
    return { opened: parsed.opened.filter((m) => typeof m === "string") };
  } catch {
    return { opened: [] };
  }
}

function writeState(state: AutoOpenState): void {
  try {
    sessionStorage.setItem(autoOpenStorageKey(), JSON.stringify(state));
  } catch {
    /* storage unavailable */
  }
}

/** `branchId:tableId:orderId` — the identity of one courtesy auto-open. */
export function autoOpenMarker(
  branchId: string,
  tableId: string,
  orderId: string,
): string {
  return `${branchId}:${tableId}:${orderId}`;
}

export function hasAutoOpened(marker: string): boolean {
  return readState().opened.includes(marker);
}

export function markAutoOpened(marker: string): void {
  const { opened } = readState();
  if (opened.includes(marker)) return;
  const next = [...opened, marker].slice(-MAX_MARKERS);
  writeState({ opened: next });
}

/** Test helper — clears every auto-open marker for the current scope. */
export function resetAutoOpened(): void {
  try {
    sessionStorage.removeItem(autoOpenStorageKey());
  } catch {
    /* storage unavailable */
  }
}

export type UseActiveTableOrderParams = {
  /** Only probe when the session is genuinely a dine-in one. */
  enabled: boolean;
  branchId: string | null | undefined;
  tableId: string | null | undefined;
};

export type UseActiveTableOrderResult = {
  /** Active order on this table, or null while unknown / absent. */
  activeOrderId: string | null;
};

export function useActiveTableOrder({
  enabled,
  branchId,
  tableId,
}: UseActiveTableOrderParams): UseActiveTableOrderResult {
  const navigate = useNavigate();
  // useNavigate() changes identity on every location change (and the dine-in
  // params injector rewrites the URL on load), so it must not be an effect
  // dependency: a re-run used to cancel the in-flight probe.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);
  // Per-mount de-dupe of the *network probe* only, set once a probe's answer
  // has been APPLIED. Unlike the auto-open marker this SHOULD reset on
  // remount — Home needs a fresh answer to decide whether the banner still
  // belongs on screen. Marking it before the answer lands made a cancelled
  // first run (StrictMode, dep change) the only probe, its answer discarded.
  const probedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !tableId || !branchId) return;
    const probeKey = `${branchId}:${tableId}`;
    if (probedRef.current === probeKey) return;

    let cancelled = false;
    tableApi
      .getActiveOrder(branchId, tableId)
      .then((result) => {
        if (cancelled) return;
        probedRef.current = probeKey;
        const orderId = result.hasActiveOrder ? result.order?.id : null;
        if (!orderId) {
          setActiveOrderId(null);
          return;
        }
        setActiveOrderId(orderId);

        const marker = autoOpenMarker(branchId, tableId, orderId);
        if (hasAutoOpened(marker)) return;
        markAutoOpened(marker);
        navigateRef.current(`/dine-in-order/${orderId}`, { replace: true });
      })
      .catch((err) => {
        console.warn("[home] active table order lookup failed", err);
      });

    return () => {
      cancelled = true;
    };
  }, [branchId, enabled, tableId]);

  return { activeOrderId };
}
